import React, { useMemo, useState, useRef, useEffect } from "react";
import { useFormikContext } from "formik";
import {
    Stack,
    Box,
    TextField,
    Chip,
} from "@mui/material";
import { fetchAiInsights } from "../API/backend";
import { Loader } from "../HelperFunctions/HelperFunctions";
import { useLoaderData } from "react-router-dom";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

function parseCcEmails(cc) {
    if (Array.isArray(cc)) {
        return cc.map((e) => String(e || "").trim()).filter(Boolean);
    }
    if (!cc) return [];
    return String(cc)
        .split(/[;,\n]+/)
        .map((e) => e.trim())
        .filter(Boolean);
}

function serializeCcEmails(emails) {
    return emails.join(";");
}

const MailComposeFeilds = () => {
    const { clientName } = useLoaderData();
    const { values, setFieldValue } = useFormikContext();
    const [loading, setLoading] = useState(false);
    const [ccInput, setCcInput] = useState("");
    const ccInputRef = useRef(null);
    let timeout;

    const ccEmails = useMemo(
        () => parseCcEmails(values.mail_compose?.cc),
        [values.mail_compose?.cc],
    );

    const setCcEmails = (emails) => {
        const unique = [];
        const seen = new Set();
        for (const email of emails) {
            const normalized = String(email || "").trim();
            if (!normalized) continue;
            const key = normalized.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            unique.push(normalized);
        }
        setFieldValue("mail_compose.cc", serializeCcEmails(unique));
    };

    const tryCommitCcEmail = (raw) => {
        const email = String(raw || "").trim().replace(/[;,]+$/, "");
        if (!email) return false;
        if (!EMAIL_RE.test(email)) return false;
        if (!ccEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
            setCcEmails([...ccEmails, email]);
        }
        setCcInput("");
        return true;
    };

    const handleCcInputChange = (event) => {
        const value = event.target.value;
        // Commit only on explicit separators — do not auto-chip while typing
        // (e.g. user still completing ...@gmail.com after ...@gmai.co).
        if (/[;,\s]$/.test(value)) {
            const candidate = value.slice(0, -1).trim();
            if (tryCommitCcEmail(candidate)) return;
            // Invalid email before separator — keep the typed text without the separator.
            setCcInput(candidate);
            return;
        }
        setCcInput(value);
    };

    const handleCcKeyDown = (event) => {
        if (event.key === "Enter") {
            if (ccInput.trim()) {
                event.preventDefault();
                if (!tryCommitCcEmail(ccInput)) {
                    // Incomplete address — leave text so user can finish typing.
                }
            }
        } else if (
            event.key === "Backspace" &&
            !ccInput &&
            ccEmails.length > 0
        ) {
            setCcEmails(ccEmails.slice(0, -1));
        }
    };

    const handleCcBlur = () => {
        if (ccInput.trim()) {
            tryCommitCcEmail(ccInput);
        }
    };

    const handleDeleteCc = (emailToDelete) => {
        setCcEmails(ccEmails.filter((email) => email !== emailToDelete));
    };

    const handleDelete = (emailToDelete) => {
        const updatedUsers = values.users.filter((email) => email !== emailToDelete);
        const updatedUsersInfo = values.users_info.filter(
            (user) => user.email !== emailToDelete,
        );
        setFieldValue("users_info", updatedUsersInfo);
        setFieldValue("users", updatedUsers);
        setFieldValue("mail_compose.to", updatedUsers);
    };

    const predefinedText = `Hello,\nI hope this email finds you well,\nPlease find attached reports.`;

    const [userText, setUserText] = useState(
        values.mail_compose.mail_body !== ""
            ? values.mail_compose.mail_body
            : predefinedText,
    );

    useEffect(() => {
        // Keep chips in sync if cc was loaded as a string from edit.
        if (
            typeof values.mail_compose?.cc === "string" &&
            values.mail_compose.cc.includes(";")
        ) {
            // already fine — parseCcEmails handles it
        }
    }, [values.mail_compose?.cc]);

    const handleChange = (event) => {
        const inputValue = event.target.value;

        if (!inputValue.startsWith(predefinedText)) {
            setUserText(predefinedText);
            setFieldValue("mail_compose.mail_body", predefinedText);
        }

        setUserText(inputValue);

        clearTimeout(timeout);
        timeout = setTimeout(() => {
            setFieldValue("mail_compose.mail_body", inputValue);
        }, 300);
    };

    const getAiInsights = async () => {
        setLoading(true);
        try {
            const corp_ids = values.selected_corporations;
            const date = new Date();
            const payload = {
                corp_ids: corp_ids,
                date,
                client_name: clientName,
            };
            const insights = await fetchAiInsights(payload);
            const newText = `${predefinedText}\n\n${insights.data.insight}`;
            setUserText(newText);
            setFieldValue("mail_compose.mail_body", newText);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    return (
        <>
            <Stack direction={"row"} gap={2}>
                <Chip
                    label="TO"
                    variant="outlined"
                    sx={{
                        borderRadius: 1,
                        width: "70px",
                        height: "30px",
                    }}
                />

                <TextField
                    variant="standard"
                    fullWidth
                    placeholder="Select users"
                    InputProps={{
                        readOnly: true,
                        startAdornment: (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                    alignItems: "center",
                                    overflow: "auto",
                                    maxHeight: "50px",
                                    minHeight: "50px",
                                    paddingY: "4px",
                                    width: "100%",
                                }}
                            >
                                {values.users.length === 0 ? (
                                    <Box
                                        sx={{
                                            fontSize: "14px",
                                            color: "text.disabled",
                                            pl: 0.5,
                                        }}
                                    >
                                        Select users
                                    </Box>
                                ) : (
                                    values.users.map((email, index) => (
                                        <Chip
                                            key={index}
                                            label={email}
                                            onDelete={() => handleDelete(email)}
                                            size="small"
                                            sx={{ fontSize: "12px", height: 24 }}
                                        />
                                    ))
                                )}
                            </Box>
                        ),
                    }}
                    sx={{
                        "& .MuiInputBase-root": {
                            alignItems: "flex-start",
                        },
                        "& .MuiInputBase-input": {
                            display: "none",
                        },
                        bgcolor: "white",
                    }}
                />
            </Stack>
            <Stack direction={"row"} gap={2}>
                <Chip
                    label="CC"
                    variant="outlined"
                    sx={{
                        borderRadius: 1,
                        width: "70px",
                        height: "30px",
                    }}
                />

                <TextField
                    variant="standard"
                    fullWidth
                    placeholder={
                        ccEmails.length === 0 ? "Enter CC email" : "Add another email"
                    }
                    value={ccInput}
                    inputRef={ccInputRef}
                    onChange={handleCcInputChange}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    InputProps={{
                        startAdornment: (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "4px",
                                    alignItems: "center",
                                    overflow: "auto",
                                    maxHeight: "50px",
                                    minHeight: "30px",
                                    paddingY: "4px",
                                    mr: 0.5,
                                }}
                                onClick={() => ccInputRef.current?.focus()}
                            >
                                {ccEmails.map((email, index) => (
                                    <Chip
                                        key={`${email}-${index}`}
                                        label={email}
                                        onDelete={() => handleDeleteCc(email)}
                                        size="small"
                                        sx={{ fontSize: "12px", height: 24 }}
                                    />
                                ))}
                            </Box>
                        ),
                    }}
                    sx={{
                        backgroundColor: "transparent",
                        width: "100%",
                        "& .MuiInputBase-root": {
                            fontSize: "14px",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                        },
                        "& .MuiInputBase-input": {
                            minWidth: "140px",
                            width: "auto",
                            flex: 1,
                            paddingTop: "8px",
                        },
                    }}
                />
            </Stack>
            <TextField
                variant="standard"
                placeholder="Add a subject"
                name="mail_compose.subject"
                value={values.mail_compose.subject}
                onChange={(e) =>
                    setFieldValue("mail_compose.subject", e.target.value)
                }
                sx={{
                    backgroundColor: "transparent",
                    width: "100%",
                    "& .MuiInputBase-root": {
                        fontSize: "14px",
                    },
                }}
            />
            <TextField
                multiline
                rows={22.3}
                variant="outlined"
                value={userText}
                onChange={handleChange}
                placeholder="Type here..."
                InputProps={{
                    style: {},
                }}
                sx={{
                    "& .MuiInputBase-root": {
                        bgcolor: "#ffffff",
                        borderRadius: 1,
                        fontSize: "12px",
                    },
                }}
            />
            {loading && <Loader loading={loading} />}
        </>
    );
};

export default MailComposeFeilds;
